import React, { useRef, useState, useEffect } from 'react';
import { OrgChartListItem } from './OrgChartListItem';
import { ButtonType } from '../../../../types/ButtonType';
import Btn from '../../../../components/molecules/Btn/Btn';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { BtnModifier } from '../../../../components/molecules/Btn';
import { Icons } from '../../../../components/atoms/SVG';
import { useDragToScroll } from './useDragToScroll';
import { IConnectedPage } from '../../../../types/api/connectedPages/IConnectedPage';
import { ConnectedPagesPageTypes } from '../../../../types/api/connectedPages/ConnectedPagesPageTypes';
import { PageType } from '../../../../types/api/PageType';
import { useSetInitialOpenPages } from '../useSetInitialOpenPages/useSetInitialOpenPages';

type OrgChartTreeProps = {
  connectedPages: IConnectedPage[];
  pageType: ConnectedPagesPageTypes;
  initialVisibleDepth: number;
  secondLevelDedicatedOrgUnitVisualizationIndices?: number[];
};

const contextFullWidthContainerClass = 'content-column-one-third--full-width';

function getShowDetailsInitialValue(
  connectedPagesOrChildPages: IConnectedPage[],
  isFullWidthPage: boolean,
) {
  if (connectedPagesOrChildPages.length === 0) {
    return true; // show details when no childPages
  }
  const childPages = connectedPagesOrChildPages[0].childPages || [];
  return (isFullWidthPage && childPages.length <= 7) || (!isFullWidthPage && childPages.length <= 4);
}

export function OrgChartTree({
  pageType,
  connectedPages = [],
  initialVisibleDepth,
}: OrgChartTreeProps) {
  // to get a nice structure we need to show the orgunit itself too, when we are not on the entry page
  const connectedPagesOrChildPages = pageType === PageType.orgunithome && connectedPages[0].childPages
    ? connectedPages[0].childPages
    : connectedPages;

  const { t } = useSonTranslation('ConnectedPages');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isFullWidthPage, setIsFullWidthPage] = useState<boolean>(false);

  useSetInitialOpenPages(connectedPagesOrChildPages, initialVisibleDepth);

  // we need to handle this query via hook/state, otherwise we get hydration errors
  useEffect(() => {
    const contextContainer = document.querySelector(`.${contextFullWidthContainerClass}`);
    setIsFullWidthPage(Boolean(contextContainer));
  }, []);

  useEffect(() => {
    setShowDetails(getShowDetailsInitialValue(connectedPagesOrChildPages || [], isFullWidthPage));
  }, [pageType, connectedPagesOrChildPages, isFullWidthPage]);

  const orgChartContentRef = useRef<HTMLDivElement>(null);

  useDragToScroll(orgChartContentRef);

  if (connectedPagesOrChildPages && connectedPagesOrChildPages.length > 0)
    return (
      <>
        <span className="org-chart-tree__button">
          <Btn
            svgSpriteId={showDetails ? Icons.MAGNIFIER_SMALLER : Icons.MAGNIFIER_BIGGER}
            type={ButtonType.BUTTON}
            text={t(showDetails ? 'viewOptions.detailsOff' : 'viewOptions.detailsOn')}
            titleText={t(showDetails ? 'viewOptions.detailsOff' : 'viewOptions.detailsOn')}
            hiddenText
            ariaControls="orgchart-content"
            modifier={BtnModifier.PULL_RIGHT}
            onClick={() => setShowDetails(!showDetails)}
          />
        </span>
        <div
          ref={orgChartContentRef}
          id="orgchart-content"
          className={`org-chart-tree ${showDetails ? '' : 'org-chart-tree--fullscreen'}`}
        >
          <ul className="org-chart-tree__list">
            {connectedPagesOrChildPages?.map(page => (
              <OrgChartListItem
                key={`pageLink-${page.pageLink.url}`}
                depth={0}
                page={page}
                showDetails={showDetails}
              />
            ))}
          </ul>
        </div>
      </>
    );

  return null;
}
